/** @flow */

import http from "@http";
import { takeLatest, put, all, call } from "redux-saga/effects";
import type { Action } from "redux-action";
import config from "@config";
import {
  ExportKpiComplianceReport,
  GetKpiComplianceReport,
} from "@modules/reporting/constants/actions";
import actionCreators from "@modules/reporting/actionCreator";
import { downloadCsvFile } from "@sagas/utils";
import type { KpiComplianceFilters } from "@modules/reporting/reducer/reports/kpi-compliance-report/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";

type KpiCompliancePayload = {
  clientId: number,
  filters: KpiComplianceFilters,
  page?: number,
};

export function* onGetKpiComplianceReport({
  payload,
}: Action<KpiCompliancePayload>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.filters) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/absence-monitoring/kpi`,
      {
        params: {
          ...payload.filters,
          page: payload.page || 1,
          limit: config.results50PerPage,
        },
      },
    );

    yield put(actionCreators.GetKpiComplianceReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetKpiComplianceReportError(e.response));
  }
}

export function* onExportKpiComplianceReport({
  payload,
}: Action<KpiCompliancePayload>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.filters) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/absence-monitoring/kpi/export`,
      {
        params: payload.filters,
        headers: { Accept: "text/csv" },
      },
    );

    yield put(actionCreators.ExportKpiComplianceReportSuccess());
    yield downloadCsvFile(
      response.data,
      `KPI-Compliance-client[${payload.clientId}]-groupBy[${payload.filters.type}]-dateFrom[${payload.filters.fromDate}]-dateTo[${payload.filters.toDate}].csv`,
    );
    yield put(sendSuccessMessage("KPI_COMPLIANCE_REPORT_EXPORT_SUCCESS"));
  } catch (e) {
    yield put(actionCreators.ExportKpiComplianceReportError(e.response));
  }
}

export default function* reportSaga(): Generator<*, void, void> {
  yield all([takeLatest(GetKpiComplianceReport, onGetKpiComplianceReport)]);
  yield all([takeLatest(ExportKpiComplianceReport, onExportKpiComplianceReport)]);
}
